<template>
  <div class="mop-picklist">
    <PickList v-model:source="sourceValue" v-model:target="targetValue" :source-header="sourceHeader"
      :target-header="targetHeader" :show-source-controls="showSourceControls"
      :show-target-controls="showTargetControls" :filter-match-mode="filterMatchMode" :filter-locale="filterLocale"
      :breakpoint="breakpoint" :meta-key-selection="metaKeySelection" :data-key="dataKey" :filter="filter"
      @reorder="$emit('reorder', $event)" @move-to-target="onMoveToTarget" @move-to-source="onMoveToSource"
      @move-all-to-target="onMoveAllToTarget" @move-all-to-source="onMoveAllToSource" :list-style="listStyle">
      <template #sourceheader>
        <slot name="sourceheader">{{ sourceHeader }}</slot>
      </template>
      <template #targetheader>
        <slot name="targetheader">{{ targetHeader }}</slot>
      </template>
      <template #item="slotProps">
        <slot name="item" :item="slotProps.item">
          {{ slotProps.item }}
        </slot>
      </template>
    </PickList>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import PickList from 'primevue/picklist'

interface Props {
  source: any[];
  target: any[];
  sourceHeader?: string;
  targetHeader?: string;
  showSourceControls?: boolean;
  showTargetControls?: boolean;
  filterMatchMode?: string;
  filterLocale?: string;
  breakpoint?: string;
  metaKeySelection?: boolean;
  dataKey?: string | undefined;
  filter?: boolean;
  listStyle?: object;
}

const props = withDefaults(defineProps<Props>(), {
  sourceHeader: 'Source',
  targetHeader: 'Target',
  showSourceControls: true,
  showTargetControls: true,
  filterMatchMode: 'contains',
  filterLocale: undefined,
  breakpoint: '960px',
  metaKeySelection: true,
  dataKey: undefined,
  filter: false,
  listStyle: () => ({ height: '24rem' })
})

const emit = defineEmits<{
  (e: 'update:source', value: any[]): void;
  (e: 'update:target', value: any[]): void;
  (e: 'reorder', event: any): void;
  (e: 'move-to-target', event: any): void;
  (e: 'move-to-source', event: any): void;
  (e: 'move-all-to-target', event: any): void;
  (e: 'move-all-to-source', event: any): void;
}>()

const sourceValue = computed({
  get: () => props.source,
  set: (value) => emit('update:source', value)
})

const targetValue = computed({
  get: () => props.target,
  set: (value) => emit('update:target', value)
})

const onMoveToTarget = (event: any) => {
  emit('move-to-target', event)
  emit('update:source', event.source)
  emit('update:target', event.target)
}

const onMoveToSource = (event: any) => {
  emit('move-to-source', event)
  emit('update:source', event.source)
  emit('update:target', event.target)
}

const onMoveAllToTarget = (event: any) => {
  emit('move-all-to-target', event)
  emit('update:source', event.source)
  emit('update:target', event.target)
}

const onMoveAllToSource = (event: any) => {
  emit('move-all-to-source', event)
  emit('update:source', event.source)
  emit('update:target', event.target)
}
</script>

<style lang="scss">
.mop-picklist {
  .p-picklist {
    display: flex;
    gap: 1rem;

    .p-picklist-list-wrapper {
      flex: 1;
    }

    .p-picklist-list {
      list-style-type: none;
      margin: 0;
      padding: 0;
      overflow: auto;
      min-height: 12rem;
      max-height: 24rem;
      width: 20rem;
      border: 1px solid var(--surface-border);
      background: var(--surface-card);
      color: var(--text-color);
      padding: 0.5rem;
      border-radius: 6px;
    }

    .p-picklist-item {
      cursor: pointer;
      overflow: hidden;
      position: relative;
      padding: 0.5rem 1rem;
      margin-bottom: 0.25rem;
      user-select: none;
      border-radius: 6px;

      &:last-child {
        margin-bottom: 0;
      }

      &.p-highlight {
        background: var(--primary-50);
        color: var(--primary-700);
      }

      &:not(.p-highlight):not(.p-disabled):hover {
        background: var(--surface-hover);
      }
    }

    .p-picklist-filter-container {
      position: relative;
      margin-bottom: 0.5rem;

      .p-picklist-filter {
        width: 100%;
        padding-right: 1.5rem;
      }

      .p-picklist-filter-icon {
        position: absolute;
        top: 50%;
        right: 0.5rem;
        transform: translateY(-50%);
        color: var(--text-color-secondary);
      }
    }

    .p-picklist-buttons {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 0.5rem;

      .p-button {
        margin-bottom: 0.5rem;
      }
    }

    &.p-picklist-responsive {
      @media screen and (max-width: 960px) {
        flex-direction: column;

        .p-picklist-buttons {
          flex-direction: row;
          padding: 1rem;
        }
      }
    }
  }
}
</style>
